/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  CapituloObra,
  IFiltroObras,
  IObraTempImpressao,
  IObras,
  ISubfiltros,
  PageObras,
} from '../types';
import ObrasSelecionadaService from '@/views/obras/service';
import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import { DataOptions } from 'vuetify';
import isEqual from 'lodash/isEqual';
import {
  transformarDadosSubFilter,
  verificarSePossuiItensSelecionadosOuExpandidos,
} from '@/utils/transform';
import { cloneDeep } from '@apollo/client/utilities';
import { useRoute } from 'vue-router/composables';
import { processarAno } from '@/utils/util';
import SecurityService from '@/services/SecurityService';
import { FL_SIM } from '@/constants';

export const ConsultaObrasStore = defineStore(
  'consultaObrasStore',

  () => {
    const route = useRoute();

    const obraTempImpressao = ref<IObraTempImpressao | null>(null);

    const listaCapitulos = ref<CapituloObra[] | null>(null);

    const listaPeriodicos = ref<any | null>(null);

    const pagina = ref(1);

    const showFilter = ref(false);

    const flProducaoCientifica = ref(false);

    const pageObras = ref<PageObras>({});

    const pageSubfiltros = ref<ISubfiltros[]>([]);

    const pageSubfiltrosOld = ref<ISubfiltros[] | undefined>(undefined);

    const dataOptions = ref({
      page: 1,
      itemsPerPage: 10,
      sortBy: [''],
      sortDesc: [true],
    } as DataOptions);

    const filtroObras = ref<IFiltroObras>({
      dePesquisaTermos: null,
      ordenacao: 'dtPublicacao DESC',
      expandirPesquisaAvancada: false,
    });

    const formattedObra = computed(
      () => `${route.params.cdObra}/${route.params.cdTipoObra}`
    );

    const obras = computed(() => {
      return pageObras.value.obras! || [];
    });

    const obrasSelecionadas = ref<IObras[]>([]);

    const codigosObrasSelecionadas = computed(() =>
      obrasSelecionadas.value.map((obra) => `${obra.cdObra}/${obra.cdTipoObra}`)
    );

    const subFiltrosCategorias = computed(() => {
      if (pageSubfiltros.value) {
        const resultadoFinal = transformarDadosSubFilter(pageSubfiltros.value);

        return resultadoFinal;
      }
      return {
        classes: [],
        relatores: [],
        comarcas: [],
        orgaosJulgadores: [],
        revistas: [],
        anos: [],
        prolatores: [],
      };
    });

    const totalHits = computed(() => pageObras.value.totalHits || 0);

    const limiteTotalHists = 10000;

    const paginacao = computed(() => {
      const page = dataOptions.value.page;
      return page - 1;
    });

    const totalElementos = computed(() => {
      if (totalHits.value > limiteTotalHists) return limiteTotalHists;
      return totalHits.value;
    });

    const params = computed<any>(() => {
      const {
        dePesquisaTermos,
        dtPublicacaoInicio,
        dtPublicacaoFim,
        relator,
        boletim,
        ordenacao,
        titulo,
        volume,
        autor,
        editora,
        assunto,
        flAcervo,
        flEpm,
        flLivro,
        flColecaoPeriodico,
        flArtigoPeriodico,
        flCapituloLivro,
      } = filtroObras.value;

      return {
        dePesquisaTermos,
        nuAnoPublicacaoInicio: dtPublicacaoInicio
          ? new Date(dtPublicacaoInicio).getTime()
          : '',
        nuAnoPublicacaoFim: dtPublicacaoFim
          ? new Date(dtPublicacaoFim).getTime()
          : '',
        flEmenta: false,
        flInteiroTeor: false,
        flPesquisaPlurais: 'N',
        deTitulo: titulo,
        deVolume: volume,
        autorSelected: autor,
        editoraSelected: editora,
        flAcervoCentral: flAcervo,
        flEPM: flEpm,
        flLivro: flLivro,
        flColecaoPeriodico: flColecaoPeriodico,
        flArtigoPeriodico: flArtigoPeriodico,
        flCapituloLivro: flCapituloLivro,
        flProdCientifica: filtroObras.value.flProducaoCientifica,
        showProdCientifica: flProducaoCientifica.value,
        legislativasNormativas: [
          {
            tipoNorma: {},
            nuNorma: '',
            deArtigo: '',
            deParagrafo: '',
            deInciso: '',
            deAlinea: '',
            deNumero: '',
          },
        ],
        numPag: paginacao.value,
        ordenacao: ordenacao,
        relatorSelected: relator ? [relator] : [],
        subFilter: subFiltrosCategorias.value,
        subFiltrosOld: pageSubfiltrosOld.value || undefined,
        tipoBoletimSelected: boletim ? [boletim] : [],
        assuntoSelected: assunto,
      };
    });

    const ordenacoes = computed(
      () =>
        pageObras.value.ordenacoes?.map((el) => ({
          text: el.label,
          value: el.value,
        })) ?? []
    );

    const ordenacaoSelecionada = computed(() => {
      return ordenacoes.value.find(
        (el) => el.value === filtroObras.value.ordenacao
      );
    });

    // Actions
    const consultaDetalhada = async () => {
      try {
        const response =
          await ObrasSelecionadaService.consultaObraParaImpressao(
            Number(route.params.cdObra),
            Number(route.params.cdTipoObra)
          );

        let disponibilidade = undefined;
        if (response.content?.deDisponibilidade) {
          disponibilidade = String(response.content?.deDisponibilidade);
        }
        obraTempImpressao.value = response.content!;
        obraTempImpressao.value!.cdObra = Number(route.params.cdObra);
        obraTempImpressao.value!.cdTipoObra = Number(route.params.cdTipoObra);
        if (disponibilidade)
          obraTempImpressao.value!.deDisponibilidade = disponibilidade
            .replaceAll('[', '')
            .replaceAll(']', '')
            .replaceAll('"', '');
      } catch (error) {
        SdsSnackbarQueue.error('Não foi possível buscar os registros');
      }
    };

    const consultaListadeCapitulos = async (evento: number) => {
      const response = await ObrasSelecionadaService.consultaCapitulosLivros(
        evento
      );
      listaCapitulos.value = response.content;
    };

    const consultaListadePeriodicos = async (evento: any) => {
      const response = await ObrasSelecionadaService.consultaListaPeriodicos(
        evento
      );
      listaPeriodicos.value = response.content;
    };

    const consultaObras = async (initialPagination = false) => {
      pageObras.value = {};
      try {
        if (initialPagination) {
          pagina.value = 1;
          dataOptions.value.page = 1;
          pageSubfiltros.value = [];
          pageSubfiltrosOld.value = undefined;
          showFilter.value = false;
          obrasSelecionadas.value = [];
        }
        const response = await ObrasSelecionadaService.consultarObras(
          params.value
        );

        if (response.hasError) {
          const msgErro = response.error?.message;
          SdsSnackbarQueue.error(
            msgErro ? msgErro : 'Não foi possível buscar os registros'
          );
          return;
        }

        pageSubfiltrosOld.value = cloneDeep(pageSubfiltros.value);

        pageObras.value = response.content!;

        const subfiltrosAtualizados = pageObras.value.subfiltros || undefined;

        if (pageSubfiltrosOld.value && pageSubfiltrosOld.value.length > 0) {
          pageSubfiltros.value = verificarSePossuiItensSelecionadosOuExpandidos(
            subfiltrosAtualizados,
            pageSubfiltrosOld.value
          );
        } else {
          pageSubfiltros.value = subfiltrosAtualizados!;
        }
      } catch (error) {
        SdsSnackbarQueue.error('Não foi possível buscar os registros');
      }
    };

    const atualizarPaginacao = async (resultadoRef: any, paginacao: number) => {
      if (!isEqual(dataOptions, paginacao)) {
        dataOptions.value.page = paginacao;
        await consultaObras();
        resultadoRef.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const atualizarOrdenacao = async (ordenacao: string) => {
      filtroObras.value.ordenacao = ordenacao;
      await consultaObras();
    };

    const limparTodosFiltroBoletins = () => {
      pageObras.value = {};
      pageSubfiltros.value = [];
      pageSubfiltrosOld.value = undefined;
      filtroObras.value = {
        dePesquisaTermos: null,
        ordenacao: 'dtPublicacao DESC',
        expandirPesquisaAvancada: false,
      };
    };

    const download = async (
      formato: 'A' | 'S' | 'D',
      obras = formattedObra.value
    ) => {
      const reportUrl = new URL(
        `${window.location.origin}/mat-report/publico/gcn/api/v1.0/obra`
      );

      reportUrl.searchParams.set('obras', obras);
      reportUrl.searchParams.set('formato', formato);

      window.open(reportUrl, '_blank');
    };

    const downloadSelecionados = async (formato: 'A' | 'S' | 'D') => {
      download(formato, codigosObrasSelecionadas.value.join(','));
    };

    const getFlProducaoCientifica = async () => {
      try {
        await SecurityService.isUsuarioLogado();
        return true;
      } catch (error) {
        const params = new URLSearchParams(window.location.search);
        return params.get('flProducaoCientifica') === FL_SIM;
      }
    };

    const setDataPublicacaoFim = (ano: number) => {
      try {
        const data = processarAno(ano);
        filtroObras.value = {
          ...filtroObras.value,
          dtPublicacaoFim: data,
        };
      } catch (error) {
        SdsSnackbarQueue.warn(error as string);
        filtroObras.value = {
          ...filtroObras.value,
          dtPublicacaoFim: undefined,
        };
      }
    };

    const setDataPublicacaoInicio = (ano: number) => {
      try {
        const data = processarAno(ano);
        filtroObras.value = {
          ...filtroObras.value,
          dtPublicacaoInicio: data,
        };
      } catch (error) {
        SdsSnackbarQueue.warn(error as string);
        filtroObras.value = {
          ...filtroObras.value,
          dtPublicacaoInicio: undefined,
        };
      }
    };

    return {
      pageObras,
      pageSubfiltros,
      pageSubfiltrosOld,
      dataOptions,
      paginacao,
      totalHits,
      obras,
      obrasSelecionadas,
      totalElementos,
      filtroObras,
      subFiltrosCategorias,
      flProducaoCientifica,
      obraTempImpressao,
      listaCapitulos,
      listaPeriodicos,
      pagina,
      showFilter,
      formattedObra,
      ordenacoes,
      ordenacaoSelecionada,
      consultaDetalhada,
      setDataPublicacaoInicio,
      setDataPublicacaoFim,
      consultaObras,
      limparTodosFiltroBoletins,
      atualizarPaginacao,
      atualizarOrdenacao,
      consultaListadeCapitulos,
      consultaListadePeriodicos,
      download,
      downloadSelecionados,
      getFlProducaoCientifica,
    };
  }
);
